/* eslint-disable react/jsx-wrap-multilines */
import React, { useMemo, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import Box from '@mui/material/Box';

import PersonAdd from '@mui/icons-material/PersonAdd';
import GroupAdd from '@mui/icons-material/GroupAdd';
import CopyIcon from '@mui/icons-material/FileCopy';

import ToMainMenuButton from '@ts-core/components/ToMainMenuButton';
import SpeedDialMenu from '@ts-core/components/SpeedDialMenu';
import ConfirmButton from '@ts-core/components/ConfirmButton';
import Dialog, { useDialog } from '@ts-core/components/Dialog';

import CourseStudentsTable, { useCourseStudentsTable } from '../../components/CourseStudentsTable';
import AddStudentForm, { useAddStudentForm } from '../../components/AddStudentForm';
import ImportStudentsFromFileForm, { useImportStudentsFromFileForm } from '../../components/ImportStudentsFromFileForm';
import ImportStudentsFromCourseForm, { useImportStudentsFromCourseForm } from '../../components/ImportStudentsFromCourse';

import { useStudents } from '../../hooks';

const StudentsPage = () => {
  const { courseId } = useParams();
  const intl = useIntl();
  const history = useHistory();

  const {
    students,
    loading,
    onRemoveStudent,
    onRemoveAllStudents,
    removingAllStudents,
    refetch: refetchStudents,
  } = useStudents({
    courseId,
  });

  const onSelectStudent = useCallback(
    (student) => {
      history.push(`students/${student._id}/assignments`);
    },
    [history]
  );

  const courseStudentsTableProps = useCourseStudentsTable({
    students,
    loading,
    onRemoveStudent,
    onSelectStudent,
  });

  /**
   * Add one student
   */
  const { onOpenDialog: onOpenAddStudentDialog, onCloseDialog: onCloseAddStudentDialog, dialogProps: addStudentDialogProps } = useDialog();
  const onStudentAdded = useCallback(() => {
    onCloseAddStudentDialog();
    refetchStudents();
  }, [onCloseAddStudentDialog, refetchStudents]);

  const addStudentFormProps = useAddStudentForm({
    courseId,
    onStudentAdded,
  });
  const { onCleanForm: onCleanAddStudentForm } = addStudentFormProps;

  const handleAddStudent = useCallback(() => {
    onCleanAddStudentForm();
    onOpenAddStudentDialog({
      title: intl.formatMessage({ id: 'addStudentForm.title' }),
    });
  }, [onOpenAddStudentDialog, onCleanAddStudentForm, intl]);

  /**
   * Import students from file
   */
  const {
    onOpenDialog: onOpenImportStudentsFromFileDialog,
    onCloseDialog: onCloseImportStudentsFromFileDialog,
    dialogProps: importStudentsFromFileDialogProps,
  } = useDialog();

  const onStudentsImportedFromFile = useCallback(() => {
    onCloseImportStudentsFromFileDialog();
    refetchStudents();
  }, [onCloseImportStudentsFromFileDialog, refetchStudents]);
  const importStudentsFromFileFormProps = useImportStudentsFromFileForm({
    courseId,
    onStudentsImported: onStudentsImportedFromFile,
  });
  const { onCleanForm: onCleanImportStudentsFromFileForm } = importStudentsFromFileFormProps;

  const handleImportStudentsFromFile = useCallback(() => {
    onCleanImportStudentsFromFileForm();
    onOpenImportStudentsFromFileDialog({
      title: intl.formatMessage({ id: 'students.button.addMultipleStudent' }),
    });
  }, [onOpenImportStudentsFromFileDialog, onCleanImportStudentsFromFileForm, intl]);

  /**
   * Import students from course
   */
  const {
    onOpenDialog: onOpenImportStudentsFromCourseForm,
    onCloseDialog: onCloseImportStudentsFromCourseForm,
    dialogProps: copyStudentsProps,
  } = useDialog();

  const onStudentsImportedFromCourse = useCallback(() => {
    onCloseImportStudentsFromCourseForm();
    refetchStudents();
  }, [onCloseImportStudentsFromCourseForm, refetchStudents]);

  const importStudentsFromCourseFormProps = useImportStudentsFromCourseForm({
    courseId,
    onStudentsImported: onStudentsImportedFromCourse,
  });

  const handleImportStudentsFromCourse = useCallback(() => {
    onOpenImportStudentsFromCourseForm({
      title: intl.formatMessage({ id: 'students.copyFromCourse' }),
    });
  }, [onOpenImportStudentsFromCourseForm, intl]);

  /**
   * Speed dial
   */
  const options = useMemo(() => {
    return [
      {
        title: <FormattedMessage id="students.button.addOneStudent" />,
        icon: <PersonAdd />,
        onClick: handleAddStudent,
      },
      {
        title: <FormattedMessage id="students.button.addMultipleStudent" />,
        icon: <GroupAdd />,
        onClick: handleImportStudentsFromFile,
      },
      {
        title: <FormattedMessage id="students.copyFromCourse" />,
        icon: <CopyIcon />,
        onClick: handleImportStudentsFromCourse,
      },
    ];
  }, [handleAddStudent, handleImportStudentsFromFile, handleImportStudentsFromCourse]);

  return (
    <>
      <Box display="flex" flexDirection="row" alignItems="center" mb={2}>
        <ToMainMenuButton title="Alumnos" />
        <Box flexGrow={1} />
        <ConfirmButton
          alertMessage={<FormattedMessage id="dialog.confirmRemoveAllStudentsFromCourse.title" />}
          onConfirm={onRemoveAllStudents}
          loading={removingAllStudents}
        >
          <FormattedMessage id="professor.studentsList.removeAll" />
        </ConfirmButton>
      </Box>
      <SpeedDialMenu options={options} />
      <CourseStudentsTable {...courseStudentsTableProps} />
      <Dialog {...addStudentDialogProps}>{() => <AddStudentForm {...addStudentFormProps} />}</Dialog>
      <Dialog {...importStudentsFromFileDialogProps}>{() => <ImportStudentsFromFileForm {...importStudentsFromFileFormProps} />}</Dialog>
      <Dialog {...copyStudentsProps}>{() => <ImportStudentsFromCourseForm {...importStudentsFromCourseFormProps} />}</Dialog>
    </>
  );
};

export default StudentsPage;
